// SCR-STAFF-607 — نگهداری، نگهداشت قانونی و امحا (US-DOC-070 تا 072؛ BR-DOC-070، BR-DOC-071): دوره هر کلاس داده با حداقل قانونی
// غیرقابل کاهش، استثنای امحا در دعاوی، و فهرست بازبینی ماهانه که فقط با تأیید Manager اجرا و گواهی امحا صادر می‌کند.
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type DocDestructionItemView,
  type DocDestructionRunDetailView,
  type DocDestructionRunView,
  type DocLegalHoldView,
  type DocRetentionClassView,
} from '@phoenix/api-client';
import { useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, faDigits, FormDialog, JalaliDate, ListPage, StatusChip, type DataTableColumn, type StatusTone } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { WriteNotice } from '../settings/common';
import { docKeys, num, SCR_STAFF_607 } from './common';

/** دوره به سال برای خواندن آسان (۲۴۰ ماه = ۲۰ سال). */
function years(months: number): string {
  return (months / 12).toFixed(months % 12 === 0 ? 0 : 1);
}

function runTone(status: string): StatusTone {
  if (status === 'Executed') {
    return 'done';
  }

  return status === 'Cancelled' ? 'cancelled' : 'pending';
}

function outcomeTone(outcome: string): StatusTone {
  if (outcome === 'Destroyed') {
    return 'done';
  }

  if (outcome === 'Failed') {
    return 'error';
  }

  return outcome === 'Pending' ? 'pending' : 'cancelled';
}

function ClassDialog({ item, onClose }: { item: DocRetentionClassView; onClose: () => void }) {
  const { t } = useTranslation('doc');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const [months, setMonths] = useState(String(num(item.months)));
  const [endAction, setEndAction] = useState<string>(item.endAction);
  const changeable = item.endAction === 'ReviewAndDestroy' || item.endAction === 'Archive';
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.PUT('/api/v1/documents/retention-classes/{code}', {
          params: { path: { code: item.code } },
          body: { months: Number(months), endAction: endAction as 'ReviewAndDestroy', rowVersion: num(item.rowVersion) },
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: docKeys.retentionClasses(session?.tenantId) });
      onClose();
    },
  });

  return (
    <FormDialog
      open
      titleKey="doc:retention_classes.edit_title"
      screenId={SCR_STAFF_607}
      onSubmit={() => save.mutate()}
      onClose={onClose}
      isDirty={months !== String(num(item.months)) || endAction !== item.endAction}
      isSubmitting={save.isPending}
      submitError={save.error}
    >
      <Typography variant="body2">{t(`doc:retention.${item.code}`)}</Typography>
      <Alert severity="info" data-testid="doc-retention-minimum">
        {t('doc:retention_classes.columns.minimum')}: {faDigits(String(num(item.minimumMonths)))} ({faDigits(years(num(item.minimumMonths)))})
      </Alert>
      <TextField
        label={t('doc:retention_classes.fields.months')}
        helperText={t('doc:retention_classes.fields.months_hint')}
        value={months}
        onChange={(event) => setMonths(event.target.value)}
        required
        slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': 'doc-retention-months' } }}
      />
      <TextField
        select
        label={t('doc:retention_classes.fields.end_action')}
        value={endAction}
        onChange={(event) => setEndAction(event.target.value)}
        disabled={!changeable}
      >
        {(changeable ? ['ReviewAndDestroy', 'Archive'] : [item.endAction]).map((value) => (
          <MenuItem key={value} value={value}>
            {t(`doc:retention_action.${value}`)}
          </MenuItem>
        ))}
      </TextField>
    </FormDialog>
  );
}

function ClassesTab({ canWrite }: { canWrite: boolean }) {
  const { t } = useTranslation('doc');
  const { tenantApi, session } = useTenantSession();
  const [editing, setEditing] = useState<DocRetentionClassView | null>(null);
  const classes = useQuery({
    queryKey: docKeys.retentionClasses(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/documents/retention-classes')),
  });

  const columns: DataTableColumn<DocRetentionClassView>[] = [
    { id: 'code', headerKey: 'doc:retention_classes.columns.code', cell: (row) => t(`doc:retention.${row.code}`) },
    { id: 'minimum', headerKey: 'doc:retention_classes.columns.minimum', cell: (row) => faDigits(years(num(row.minimumMonths))) },
    {
      id: 'period',
      headerKey: 'doc:retention_classes.columns.period',
      cell: (row) => <span data-testid={`doc-retention-period-${row.code}`}>{faDigits(years(num(row.months)))}</span>,
    },
    { id: 'origin', headerKey: 'doc:retention_classes.columns.origin', cell: (row) => t(`doc:retention_origin.${row.origin}`) },
    {
      id: 'end_action',
      headerKey: 'doc:retention_classes.columns.end_action',
      cell: (row) => t(`doc:retention_action.${row.endAction}`),
    },
    { id: 'policy', headerKey: 'doc:retention_classes.columns.policy', cell: (row) => faDigits(row.policyRows.join(t('common:list_separator'))) },
    {
      id: 'subjects',
      headerKey: 'doc:retention_classes.columns.subjects',
      cell: (row) => (
        <span data-testid={`doc-retention-subjects-${row.code}`}>
          {t('doc:retention_classes.subjects', { count: row.subjects.length })}
        </span>
      ),
    },
    {
      id: 'actions',
      headerKey: 'doc:retention_classes.columns.actions',
      cell: (row) =>
        canWrite ? (
          <Button size="small" onClick={() => setEditing(row)} data-testid={`doc-retention-edit-${row.code}`}>
            {t('doc:retention_classes.edit')}
          </Button>
        ) : null,
    },
  ];


  return (
    <Stack spacing={2}>
      <Alert severity="info">{t('doc:retention_page.policy_note')}</Alert>
      <DataTable
        captionKey="doc:retention_classes.caption"
        emptyTitleKey="doc:retention_classes.empty"
        columns={columns}
        rows={classes.data?.items}
        getRowId={(row) => row.code}
        isLoading={classes.isPending}
        error={classes.error}
        onRetry={() => void classes.refetch()}
      />
      {editing ? <ClassDialog item={editing} onClose={() => setEditing(null)} /> : null}
    </Stack>
  );
}

function HoldDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation('doc');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const [scope, setScope] = useState<string>('Patient');
  const [targetId, setTargetId] = useState('');
  const [reason, setReason] = useState('');
  const [caseReference, setCaseReference] = useState('');
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/documents/legal-holds', {
          body: { scope: scope as 'Patient', targetId, reason, caseReference: caseReference || null },
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: docKeys.all(session?.tenantId) });
      onClose();
    },
  });

  return (
    <FormDialog
      open
      titleKey="doc:legal_holds.add_title"
      screenId={SCR_STAFF_607}
      onSubmit={() => save.mutate()}
      onClose={onClose}
      isDirty={!!targetId || !!reason}
      isSubmitting={save.isPending}
      submitError={save.error}
    >
      <TextField
        select
        label={t('doc:legal_holds.fields.scope')}
        value={scope}
        onChange={(event) => setScope(event.target.value)}
        slotProps={{ htmlInput: { 'data-testid': 'doc-hold-scope' } }}
      >
        {['Patient', 'Document'].map((value) => (
          <MenuItem key={value} value={value}>
            {t(`doc:legal_hold_scope.${value}`)}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        label={t('doc:legal_holds.fields.target')}
        value={targetId}
        onChange={(event) => setTargetId(event.target.value)}
        required
        slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'doc-hold-target' } }}
      />
      <TextField
        label={t('doc:legal_holds.fields.reason')}
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        required
        multiline
        minRows={2}
        slotProps={{ htmlInput: { 'data-testid': 'doc-hold-reason' } }}
      />
      <TextField
        label={t('doc:legal_holds.fields.case')}
        value={caseReference}
        onChange={(event) => setCaseReference(event.target.value)}
        slotProps={{ htmlInput: { 'data-testid': 'doc-hold-case' } }}
      />
    </FormDialog>
  );
}

function HoldsTab({ canWrite }: { canWrite: boolean }) {
  const { t } = useTranslation('doc');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const [onlyActive, setOnlyActive] = useState(false);
  const [adding, setAdding] = useState(false);
  const holds = useQuery({
    queryKey: docKeys.legalHolds(session?.tenantId, onlyActive),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/documents/legal-holds', { params: { query: { onlyActive } } })),
  });
  const release = useMutation({
    mutationFn: (id: string) =>
      unwrap(tenantApi.POST('/api/v1/documents/legal-holds/{id}:release', { params: { path: { id } }, body: { note: null } })),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: docKeys.all(session?.tenantId) }),
  });

  const columns: DataTableColumn<DocLegalHoldView>[] = [
    { id: 'scope', headerKey: 'doc:legal_holds.columns.scope', cell: (row) => t(`doc:legal_hold_scope.${row.scope}`) },
    { id: 'target', headerKey: 'doc:legal_holds.columns.target', cell: (row) => <span dir="ltr">{row.targetId.slice(0, 8)}</span> },
    { id: 'reason', headerKey: 'doc:legal_holds.columns.reason', cell: (row) => row.reason },
    { id: 'case', headerKey: 'doc:legal_holds.columns.case', cell: (row) => (row.caseReference ? faDigits(row.caseReference) : '—') },
    { id: 'placed', headerKey: 'doc:legal_holds.columns.placed', cell: (row) => <JalaliDate value={row.placedAt} /> },
    {
      id: 'status',
      headerKey: 'doc:legal_holds.columns.status',
      cell: (row) => (
        <span data-testid={`doc-hold-status-${row.id}`}>
          <StatusChip tone={row.isActive ? 'active' : 'cancelled'} labelKey={row.isActive ? 'doc:legal_holds.active' : 'doc:legal_holds.released'} />
        </span>
      ),
    },
    {
      id: 'affected',
      headerKey: 'doc:legal_holds.columns.affected',
      cell: (row) => t('doc:legal_holds.affected', { count: num(row.affectedDocuments) }),
    },
    {
      id: 'actions',
      headerKey: 'doc:legal_holds.columns.actions',
      cell: (row) =>
        canWrite && row.isActive ? (
          <Button size="small" onClick={() => release.mutate(row.id)} data-testid={`doc-hold-release-${row.id}`}>
            {t('doc:legal_holds.release')}
          </Button>
        ) : null,
    },
  ];


  return (
    <Stack spacing={2}>
      <Alert severity="warning">{t('doc:legal_holds.note')}</Alert>
      <Stack direction="row" spacing={2} alignItems="center">
        <FormControlLabel
          control={<Checkbox checked={onlyActive} onChange={(event) => setOnlyActive(event.target.checked)} data-testid="doc-hold-only-active" />}
          label={t('doc:legal_holds.only_active')}
        />
        {canWrite ? (
          <Button variant="contained" onClick={() => setAdding(true)} data-testid="doc-hold-add">
            {t('doc:legal_holds.add')}
          </Button>
        ) : null}
      </Stack>
      <DataTable
        captionKey="doc:legal_holds.caption"
        emptyTitleKey="doc:legal_holds.empty"
        columns={columns}
        rows={holds.data?.items}
        getRowId={(row) => row.id}
        isLoading={holds.isPending}
        error={holds.error}
        onRetry={() => void holds.refetch()}
      />
      {adding ? <HoldDialog onClose={() => setAdding(false)} /> : null}
    </Stack>
  );
}

function Certificate({ detail }: { detail: DocDestructionRunDetailView }) {
  const { t } = useTranslation('doc');

  if (!detail.certificate) {
    return null;
  }

  const certificate = detail.certificate;
  const rows: [string, string][] = [
    ['number', faDigits(certificate.number)],
    ['documents', faDigits(String(num(certificate.documentsDestroyed)))],
    ['patients', faDigits(String(num(certificate.patientsAnonymized)))],
    ['files', faDigits(String(num(certificate.filesDeleted)))],
    ['held', faDigits(String(num(certificate.skippedByLegalHold)))],
    ['classes', certificate.classes.map((code) => t(`doc:retention.${code}`)).join(t('common:list_separator'))],
    ['digest', certificate.digest],
  ];

  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="doc-destruction-certificate">
      <Typography variant="subtitle2" gutterBottom>
        {t('doc:destruction.certificate')}
      </Typography>
      <Stack spacing={0.5}>
        {rows.map(([key, value]) => (
          <Typography key={key} variant="body2">
            {t(`doc:destruction.certificate_fields.${key}`)}: <span dir={key === 'digest' ? 'ltr' : undefined}>{value}</span>
          </Typography>
        ))}
        <Typography variant="caption" color="text.secondary">
          {t('doc:destruction.certificate_fields.backup')}: {certificate.backupNote}
        </Typography>
      </Stack>
    </Paper>
  );
}

function ApproveDialog({ detail, onClose }: { detail: DocDestructionRunDetailView; onClose: () => void }) {
  const { t } = useTranslation('doc');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const [excluded, setExcluded] = useState<string[]>([]);
  const pending = detail.items.filter((item) => item.outcome === 'Pending');
  const approve = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/documents/destruction-runs/{id}:approve', {
          params: { path: { id: detail.run.id } },
          body: { excludedItemIds: excluded },
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: docKeys.all(session?.tenantId) });
      onClose();
    },
  });

  return (
    <FormDialog
      open
      titleKey="doc:destruction.approve_title"
      screenId={SCR_STAFF_607}
      onSubmit={() => approve.mutate()}
      onClose={onClose}
      isDirty
      isSubmitting={approve.isPending}
      submitError={approve.error}
      submitKey="doc:destruction.approve"
      maxWidth="md"
    >
      <Alert severity="warning" data-testid="doc-destruction-warning">
        {t('doc:destruction.approve_warning')}
      </Alert>
      <Stack spacing={0.5}>
        {pending.map((item) => (
          <FormControlLabel
            key={item.id}
            control={
              <Checkbox
                checked={excluded.includes(item.id)}
                onChange={(event) => setExcluded((current) => (event.target.checked ? [...current, item.id] : current.filter((id) => id !== item.id)))}
                data-testid={`doc-destruction-exclude-${item.id}`}
              />
            }
            label={`${t(`doc:destruction_kind.${item.kind}`)} · ${t(`doc:retention.${item.retentionClass}`)} · ${item.targetId.slice(0, 8)}`}
          />
        ))}
      </Stack>
    </FormDialog>
  );
}

function RunDetail({ runId }: { runId: string }) {
  const { t } = useTranslation('doc');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const canWrite = useWriteAccess('doc.retention.destroy').canWrite;
  const [approving, setApproving] = useState(false);
  const detail = useQuery({
    queryKey: docKeys.destructionRun(session?.tenantId, runId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/documents/destruction-runs/{id}', { params: { path: { id: runId } } })),
  });
  const cancel = useMutation({
    mutationFn: () =>
      unwrap(tenantApi.POST('/api/v1/documents/destruction-runs/{id}:cancel', { params: { path: { id: runId } }, body: { note: null } })),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: docKeys.all(session?.tenantId) }),
  });

  const columns: DataTableColumn<DocDestructionItemView>[] = [
    { id: 'kind', headerKey: 'doc:destruction.item_columns.kind', cell: (row) => t(`doc:destruction_kind.${row.kind}`) },
    { id: 'target', headerKey: 'doc:destruction.item_columns.target', cell: (row) => <span dir="ltr">{row.targetId.slice(0, 8)}</span> },
    { id: 'type', headerKey: 'doc:destruction.item_columns.type', cell: (row) => row.typeCode ?? '—' },
    { id: 'class', headerKey: 'doc:destruction.item_columns.class', cell: (row) => t(`doc:retention.${row.retentionClass}`) },
    { id: 'due', headerKey: 'doc:destruction.item_columns.due', cell: (row) => <JalaliDate value={row.dueOn} /> },
    {
      id: 'outcome',
      headerKey: 'doc:destruction.item_columns.outcome',
      cell: (row) => (
        <span data-testid={`doc-destruction-outcome-${row.id}`}>
          <StatusChip tone={outcomeTone(row.outcome)} labelKey={`doc:destruction_outcome.${row.outcome}`} />
        </span>
      ),
    },
    { id: 'files', headerKey: 'doc:destruction.item_columns.files', cell: (row) => faDigits(String(num(row.filesDeleted))) },
    { id: 'note', headerKey: 'doc:destruction.item_columns.note', cell: (row) => (row.note ? <span dir="ltr">{row.note}</span> : '—') },
  ];


  return (
    <Stack spacing={2} data-testid="doc-destruction-detail">
      {detail.data?.run.status === 'PendingApproval' && canWrite ? (
        <Stack direction="row" spacing={2}>
          <Button variant="contained" color="error" onClick={() => setApproving(true)} data-testid="doc-destruction-approve">
            {t('doc:destruction.approve')}
          </Button>
          <Button onClick={() => cancel.mutate()} data-testid="doc-destruction-cancel">
            {t('doc:destruction.cancel')}
          </Button>
        </Stack>
      ) : null}
      <DataTable
        captionKey="doc:destruction.items"
        emptyTitleKey="doc:destruction.empty"
        columns={columns}
        rows={detail.data?.items}
        getRowId={(row) => row.id}
        isLoading={detail.isPending}
        error={detail.error}
        onRetry={() => void detail.refetch()}
      />
      {detail.data ? <Certificate detail={detail.data} /> : null}
      {approving && detail.data ? <ApproveDialog detail={detail.data} onClose={() => setApproving(false)} /> : null}
    </Stack>
  );
}

function RunsTab({ canWrite }: { canWrite: boolean }) {
  const { t } = useTranslation('doc');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [nothingDue, setNothingDue] = useState(false);
  const runs = useQuery({
    queryKey: docKeys.destructionRuns(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/documents/destruction-runs')),
  });
  const build = useMutation({
    mutationFn: () => tenantApi.POST('/api/v1/documents/destruction-runs', {}),
    onSuccess: (result) => {
      const detail = result.data as DocDestructionRunDetailView | undefined;
      setNothingDue(!detail);

      if (detail) {
        setSelected(detail.run.id);
      }

      void queryClient.invalidateQueries({ queryKey: docKeys.all(session?.tenantId) });
    },
  });

  const columns: DataTableColumn<DocDestructionRunView>[] = [
    { id: 'number', headerKey: 'doc:destruction.columns.number', cell: (row) => faDigits(row.number) },
    { id: 'period', headerKey: 'doc:destruction.columns.period', cell: (row) => <JalaliDate value={row.periodEnd} /> },
    {
      id: 'status',
      headerKey: 'doc:destruction.columns.status',
      cell: (row) => (
        <span data-testid={`doc-run-status-${row.id}`}>
          <StatusChip tone={runTone(row.status)} labelKey={`doc:destruction_status.${row.status}`} />
        </span>
      ),
    },
    { id: 'total', headerKey: 'doc:destruction.columns.total', cell: (row) => faDigits(String(num(row.totalItems))) },
    { id: 'pending', headerKey: 'doc:destruction.columns.pending', cell: (row) => faDigits(String(num(row.pendingItems))) },
    { id: 'held', headerKey: 'doc:destruction.columns.held', cell: (row) => faDigits(String(num(row.heldItems))) },
    { id: 'destroyed', headerKey: 'doc:destruction.columns.destroyed', cell: (row) => faDigits(String(num(row.destroyedItems))) },
    {
      id: 'actions',
      headerKey: 'doc:destruction.columns.actions',
      cell: (row) => (
        <Button size="small" onClick={() => setSelected(row.id)} data-testid={`doc-run-open-${row.id}`}>
          {t('doc:destruction.open')}
        </Button>
      ),
    },
  ];


  return (
    <Stack spacing={2}>
      {canWrite ? (
        <Stack direction="row" spacing={2} alignItems="center">
          <Button variant="outlined" onClick={() => build.mutate()} disabled={build.isPending} data-testid="doc-destruction-build">
            {t('doc:destruction.build')}
          </Button>
          {nothingDue ? <Alert severity="success" data-testid="doc-destruction-nothing">{t('doc:destruction.nothing_due')}</Alert> : null}
        </Stack>
      ) : null}
      <DataTable
        captionKey="doc:destruction.caption"
        emptyTitleKey="doc:destruction.empty"
        columns={columns}
        rows={runs.data?.items}
        getRowId={(row) => row.id}
        isLoading={runs.isPending}
        error={runs.error}
        onRetry={() => void runs.refetch()}
      />
      {selected ? <RunDetail runId={selected} /> : null}
    </Stack>
  );
}

/** SCR-STAFF-607. */
export function RetentionPage() {
  const { t } = useTranslation('doc');
  const classWrite = useWriteAccess('doc.retention.manage');
  const holdWrite = useWriteAccess('doc.legal_hold.manage');
  const destroyWrite = useWriteAccess('doc.retention.destroy');
  const [tab, setTab] = useState(0);
  const write = [classWrite, holdWrite, destroyWrite][tab]!;

  return (
    <ListPage screenId={SCR_STAFF_607} titleKey="doc:retention_page.title" subtitleKey="doc:retention_page.subtitle">
      <WriteNotice write={write} />
      <Tabs value={tab} onChange={(_, value: number) => setTab(value)} sx={{ mb: 2 }}>
        <Tab label={t('doc:retention_page.tab_classes')} data-testid="doc-tab-classes" />
        <Tab label={t('doc:retention_page.tab_holds')} data-testid="doc-tab-holds" />
        <Tab label={t('doc:retention_page.tab_runs')} data-testid="doc-tab-runs" />
      </Tabs>
      {tab === 0 ? <ClassesTab canWrite={classWrite.canWrite} /> : null}
      {tab === 1 ? <HoldsTab canWrite={holdWrite.canWrite} /> : null}
      {tab === 2 ? <RunsTab canWrite={destroyWrite.canWrite} /> : null}
    </ListPage>
  );
}
